import { CanActivateFn, Router } from '@angular/router';
import { inject } from '@angular/core';
import { AuthenticationService } from '../services/auth.service';
import { LocalStorageService } from '../services/local-storage.service';
import { AlertService } from '../services/alert.service';
import { UnauthorizedAccessHandler } from '../utils/unauthorized-access-handler';
import { ensureInitWithDeviceDelay, clearAllAndRedirect, allowIf, handleGuardFatal } from './guard-helpers';
import { isServiceProvider as checkIsServiceProvider } from '../models/interfaces';
import { switchMap, catchError } from 'rxjs/operators';
import { of } from 'rxjs';

/**
 * Admin guard - requires admin or service provider role
 */
export const adminGuard: CanActivateFn = (route, state) => {
  const authService = inject(AuthenticationService);
  const router = inject(Router);
  const storage = inject(LocalStorageService);
  const alert = inject(AlertService);

  return ensureInitWithDeviceDelay(authService).pipe(
    switchMap(() => authService.isAuthenticated$()),
    switchMap((isAuthenticated: boolean) => {
      if (!isAuthenticated) {
        return clearAllAndRedirect(storage, router, ['/auth/login'], alert, 'Access Denied', 'Please login to access admin features.');
      }
      const user = authService.currentUser;
      const isAdmin = authService.isAdmin();
      const userRoles = user?.roles || [];
      const isServiceProvider = checkIsServiceProvider(userRoles);

      // If user doesn't have required role, redirect to unauthorized page
      if (!isAdmin && !isServiceProvider) {
        UnauthorizedAccessHandler.redirectWithRole(
          state.url,
          'Admin or Service Provider',
          router
        );
        return of(false);
      }

      return of(true);
    }),
    catchError(() => handleGuardFatal(storage, router, ['/']))
  );
};